import { ArrowRight, CheckCircle2, MapPin, Home, Ruler, Hammer } from "lucide-react";
import Navigation from "@/components/Navigation";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import MobileStickyBar from "@/components/MobileStickyBar";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import Seo, { businessSchema } from "@/components/seo/Seo";

export default function CoveringAinPage() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Covering cuisine dans l'Ain",
    provider: { "@type": "Organization", name: "Vinylux", url: "https://www.vinylux-covering.fr/" },
    areaServed: ["Ain", "Sainte-Olive", "Reyrieux", "Trévoux", "Ambérieux-en-Dombes", "Bourg-en-Bresse"],
    description: "Rénovation et covering de cuisine dans l'Ain par Vinylux, artisan basé à Sainte-Olive.",
    url: "https://www.vinylux-covering.fr/covering-cuisine-ain",
  };

  const zones = ["Sainte-Olive", "Reyrieux", "Trévoux", "Ambérieux-en-Dombes", "Villars-les-Dombes", "Bourg-en-Bresse"];

  return (
    <div className="min-h-screen bg-[#0284FC]">
      <Seo
        title="Covering cuisine Ain (01) | Artisan local – Vinylux"
        description="Vinylux, artisan basé à Sainte-Olive, rénove les cuisines dans l'Ain par covering : façades, joues, plans de travail et crédences selon faisabilité. Devis gratuit."
        path="/covering-cuisine-ain"
        schema={[businessSchema, serviceSchema]}
      />
      <Navigation />
      <main className="pb-20 lg:pb-0">
        <section className="pt-32 lg:pt-[188px] pb-20 lg:pb-28 bg-[#0284FC]">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/15 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-7">
                <MapPin className="w-4 h-4" /> Basé à Sainte-Olive (01330)
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05] mb-6">
                Covering cuisine dans l'Ain
              </h1>
              <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-2xl">
                Vous gardez une cuisine saine et fonctionnelle, mais vous voulez changer complètement son style ? Vinylux intervient dans l'Ain pour rénover les surfaces visibles sans remplacer tous les meubles.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact" className="inline-flex items-center justify-center gap-2 bg-white text-[#0284FC] font-bold px-8 py-4 rounded-full hover:scale-105 transition-all">
                  Demander un devis gratuit <ArrowRight className="w-5 h-5" />
                </a>
                <a href="/realisations" className="inline-flex items-center justify-center border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-all">
                  Voir les réalisations
                </a>
              </div>
            </div>

            <div className="grid gap-4">
              {[
                { icon: Home, title: "Proximité", text: "Une intervention locale dans l'Ain et la Dombes, avec un interlocuteur unique." },
                { icon: Ruler, title: "Devis sur mesure", text: "Le chiffrage est basé sur les dimensions, le nombre de façades et l'état réel des supports." },
                { icon: Hammer, title: "Approche de menuisier", text: "Avant de poser, je vérifie la stabilité, les chants, les formes et les réparations nécessaires." },
              ].map(({ icon: Icon, title, text }) => (
                <div key={title} className="bg-white/10 border border-white/15 rounded-2xl p-6 flex gap-4">
                  <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-white" /></div>
                  <div><h2 className="font-bold text-white text-lg mb-1">{title}</h2><p className="text-white/70 leading-relaxed">{text}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28 bg-[#0273DB]">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">Une rénovation pensée pour conserver ce qui fonctionne</h2>
                <div className="space-y-5 text-white/75 leading-relaxed text-base sm:text-lg">
                  <p>Quand les caissons et les façades sont sains, les remplacer uniquement pour changer de couleur ou de style n'est pas toujours rationnel. Le covering permet de transformer l'apparence tout en conservant la base existante.</p>
                  <p>Je contrôle d'abord le support : adhérence, état des chants, reliefs, arrondis et éventuelles réparations. Si un élément n'est pas adapté, je préfère le dire avant le chantier plutôt que promettre un résultat fragile.</p>
                  <p>Selon le projet, nous pouvons travailler les façades, tiroirs, joues, plinthes, plans de travail et crédences lorsque le support est compatible.</p>
                </div>
              </div>
              <div className="bg-white/10 border border-white/15 rounded-3xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Secteurs régulièrement couverts</h3>
                <div className="flex flex-wrap gap-3 mb-7">
                  {zones.map((zone) => <span key={zone} className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-white/85 px-4 py-2 rounded-full text-sm"><MapPin className="w-4 h-4" />{zone}</span>)}
                </div>
                <ul className="space-y-3">
                  {["Déplacement sur rendez-vous", "Étude à partir de photos possible avant visite", "Choix des finitions après validation technique", "Devis détaillé avant intervention"].map((item) => <li key={item} className="flex gap-3 text-white/80"><CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />{item}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <BeforeAfterSlider />
        <ContactSection />
      </main>
      <Footer />
      <MobileStickyBar />
    </div>
  );
}
